import React, { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Bounce, toast } from "react-toastify"
import Pagination from "../../../components/Listing/Pagination"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { MAILGUN_ROWS } from "./adminMailGunData"
import MailGunModal from "./MailGunModal"

const PAGE_SIZE = 15

const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

const formatTimestamp = () =>
  new Date().toLocaleString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })

/**
 * Admin MailGun listing — Figma (`40005433:20000`).
 */
const AdminMailGun = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [rows, setRows] = useState(MAILGUN_ROWS)
  const [currentPage, setCurrentPage] = useState(1)
  const [modalOpen, setModalOpen] = useState(
    () => searchParams.get("create") === "1",
  )
  const [editingConfig, setEditingConfig] = useState(null)

  useEffect(() => {
    document.title = "MailGun | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    if (searchParams.get("create") !== "1") return
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("create")
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  const totalItems = rows.length
  const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE))
  const safePage = Math.min(currentPage, pageCount)
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage],
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [rows.length])

  const openAddModal = () => {
    setEditingConfig(null)
    setModalOpen(true)
  }

  const openEditModal = row => {
    setEditingConfig(row)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditingConfig(null)
  }

  const handleSave = payload => {
    const timestamp = formatTimestamp()

    if (payload.id) {
      setRows(prev =>
        prev.map(row =>
          row.id === payload.id
            ? {
                ...row,
                ...payload,
                updatedAt: timestamp,
              }
            : row,
        ),
      )
      return
    }

    setRows(prev => [
      {
        ...payload,
        id: String(Date.now()),
        createdAt: timestamp,
        updatedAt: timestamp,
      },
      ...prev,
    ])
  }

  const handleDelete = row => {
    setRows(prev => prev.filter(item => item.id !== row.id))
    toast.success(`“${row.domain}” removed.`, {
      transition: Bounce,
    })
  }

  return (
    <div className="admin-page admin-mailgun">
      <div className="admin-mailgun__toolbar">
        <h1 className="admin-mailgun__title">MailGun</h1>
        <div className="admin-mailgun__toolbar-actions">
          <ExportLogButton entityLabel="mailgun" />
          <CustomButton
            variant="primary"
            size="md"
            type="button"
            trailingIconSrc={ICONS.plus}
            trailingIconAlt=""
            onClick={openAddModal}
          >
            Add MailGun
          </CustomButton>
        </div>
      </div>

      <div className="admin-mailgun__table-wrapper">
        <div className="admin-mailgun__table" role="table">
          <div
            className="admin-mailgun__table-row admin-mailgun__table-row--head"
            role="row"
          >
            <div role="columnheader">ID</div>
            <div role="columnheader">Domain</div>
            <div role="columnheader">Private Key</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Created At</div>
            <div role="columnheader">Updated At</div>
            <div role="columnheader" className="admin-mailgun__actions-head">
              Actions
            </div>
          </div>

          {pageRows.map(row => (
            <div key={row.id} className="admin-mailgun__table-row" role="row">
              <TruncatedText as="div" className="admin-mailgun__cell" role="cell">
                {row.id}
              </TruncatedText>
              <TruncatedText as="div" className="admin-mailgun__cell" role="cell">
                {row.domain}
              </TruncatedText>
              <TruncatedText
                as="div"
                className="admin-mailgun__cell admin-mailgun__cell--key"
                role="cell"
              >
                {row.privateKey}
              </TruncatedText>
              <div className="admin-mailgun__status-cell" role="cell">
                <span
                  className={`admin-mailgun__status admin-mailgun__status--${row.status}`}
                >
                  {STATUS_LABEL[row.status] || row.status}
                </span>
              </div>
              <TruncatedText as="div" className="admin-mailgun__cell" role="cell">
                {row.createdAt}
              </TruncatedText>
              <TruncatedText as="div" className="admin-mailgun__cell" role="cell">
                {row.updatedAt}
              </TruncatedText>
              <div className="admin-mailgun__actions" role="cell">
                <CustomIconButton
                  iconSrc={ICONS.edit}
                  label={`Edit ${row.domain}`}
                  size="sm"
                  onClick={() => openEditModal(row)}
                />
                <CustomIconButton
                  iconSrc={ICONS.trash}
                  label={`Delete ${row.domain}`}
                  tone="danger"
                  size="sm"
                  onClick={() => handleDelete(row)}
                />
              </div>
            </div>
          ))}

          {pageRows.length === 0 && (
            <div className="admin-mailgun__empty">
              <EmptyState
                title="No MailGun configurations found"
                description="Add a MailGun API configuration to get started."
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={safePage}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}
        onPageChange={setCurrentPage}
        itemLabel="Configurations"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-mailgun__pagination"
      />

      <MailGunModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSave={handleSave}
        config={editingConfig}
      />
    </div>
  )
}

export default AdminMailGun
